"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsInsights = exports.facebookAdsInsightsResultSchema = exports.facebookAdsInsightsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var facebookAdsInsightsInputSchema = zod_1.z
    .object({
    task: zod_1.z
        .string()
        .describe('Natural language description of what to analyze. Examples: "analyze my top campaigns this month", "find underperforming ads", "review budget allocation across ad sets"'),
    level: zod_1.z
        .enum(['campaign', 'adset', 'ad'])
        .nullish()
        .describe('Optional hint for what level to analyze. Agent can override based on task context. Options: campaign (campaign-level analysis), adset (ad set-level analysis), ad (individual ad analysis)'),
    entityIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Optional specific campaign/adset/ad IDs to analyze. If provided, only these entities will be fetched. e.g. ["123456789", "987654321"]'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for analysis in YYYY-MM-DD format. e.g. 2025-01-01. If not provided, defaults to 30 days ago.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for analysis in YYYY-MM-DD format. e.g. 2025-01-31. If not provided, defaults to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('Maximum number of entities to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    focusArea: zod_1.z
        .enum(['performance', 'budget', 'targeting', 'creative', 'general'])
        .nullish()
        .describe('Focus area for insights: performance (CTR, CPC optimization), budget (spend efficiency), targeting (audience analysis), creative (ad copy/visuals), or general (overall review). Unless specified, defaults to general.'),
})
    .describe('Parameters for AI-powered Facebook Ads performance insights and recommendations. Use for analysis, recommendations, performance reviews, optimization questions. Do not use for raw data export.');
exports.facebookAdsInsightsArgsSchema = zod_1.z
    .object({
    args: facebookAdsInsightsInputSchema,
    emitMode: common_1.emitModeSchema,
})
    .describe('Parameters for AI-powered Facebook Ads performance insights and recommendations. Use for analysis, recommendations, performance reviews, optimization questions. Do not use for raw data export.');
exports.facebookAdsInsightsResultSchema = zod_1.z.object({
    summary: zod_1.z.string().describe('Overall summary of insights and recommendations'),
    insights: zod_1.z
        .array(zod_1.z.object({
        category: zod_1.z
            .string()
            .describe('Category of insight (e.g., Performance, Budget, Targeting, Creative)'),
        finding: zod_1.z.string().describe('Key finding or observation'),
        recommendation: zod_1.z.string().describe('Recommended action to improve'),
        priority: zod_1.z.enum(['high', 'medium', 'low']).describe('Priority level'),
        impact: zod_1.z.string().describe('Expected impact of implementing the recommendation'),
    }))
        .describe('Detailed insights and recommendations'),
    topPerformers: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        metric: zod_1.z.string(),
        value: zod_1.z.number(),
        description: zod_1.z.string(),
    }))
        .nullish()
        .describe('Top performing entities'),
    underperformers: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        metric: zod_1.z.string(),
        value: zod_1.z.number(),
        description: zod_1.z.string(),
    }))
        .nullish()
        .describe('Underperforming entities that need attention'),
    performanceData: zod_1.z
        .object({
        campaigns: zod_1.z.array(zod_1.z.any()).nullish().describe('Campaign performance data'),
        adsets: zod_1.z.array(zod_1.z.any()).nullish().describe('Ad set performance data'),
        ads: zod_1.z.array(zod_1.z.any()).nullish().describe('Ad performance data'),
        dateRange: zod_1.z
            .object({
            start: zod_1.z.string(),
            end: zod_1.z.string(),
        })
            .nullish()
            .describe('Date range for the performance data'),
    })
        .nullish()
        .describe('Raw performance data fetched during analysis'),
});
exports.facebookAdsInsights = (0, common_1.createEmitModeToolMetadata)({
    id: 'facebookAdsInsights',
    displayName: 'Facebook Ads Insights & Recommendations',
    displayDescription: 'Analyze Facebook Ads performance using AI-powered insights. Agent intelligently fetches relevant data (campaigns, ad sets, or ads) based on your request, then provides comprehensive analysis with actionable recommendations for optimization.',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsInsightsArgsSchema,
}, exports.facebookAdsInsightsResultSchema);
